import { useTranslation } from "next-i18next";
import ButtonFilled from "../components/ui_components/buttonFilled";
import PageTitle from "../components/ui_components/pageTitle";
import React, { useEffect, useState } from "react";
import { useGlobalState } from "../context/GlobalStateContext";
import { apiClient } from "../helpers/api-client";
import * as validators from "../helpers/form/validators";
import ContactImage from "components/customImages/ContactImage";
import { h2FontSize } from "utils/common";

const Contact = ({ contactsRef }) => {
  const { t: translate } = useTranslation("contact");
  const { addToaster, removeToaster } = useGlobalState();
  const { direction } = useGlobalState();

  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [nameError, setNameError] = useState<string>("");
  const [messageError, setMessageError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const submit = () => {
    if (isSubmitted || isInvalidForm()) {
      return;
    }

    setIsSubmitted(true);
    apiClient
      .post({ uri: "/email", body: formData })
      .then(() => {
        addToaster({
          message: translate("sendMailSuccess"),
          type: "success",
          callback: (index: number) => removeToaster(index),
        });
        setFormData({ name: "", email: "", message: "" });
      })
      .catch((error) => {
        console.error(error);
        addToaster({
          message: translate("sendMailFail"),
          type: "error",
          callback: (index: number) => removeToaster(index),
        });
      })
      .finally(() => setIsSubmitted(false));
  };

  const validateName = (value: string) => setNameError(validators.validateContactName(value));
  const validateEmail = (value: string) => setEmailError(validators.validateEmail(value, true));
  const validateMessage = (value: string) =>
    setMessageError(validators.validateContactMessage(value));

  const handleChange = ({
    target: { id, value },
  }: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData((prevState) => ({ ...prevState, [id]: value }));
    switch (id) {
      case "name":
        validateName(value);
        break;
      case "email":
        validateEmail(value);
        break;
      case "message":
        validateMessage(value);
        break;
      default:
        break;
    }
  };

  const isInvalidForm = () => {
    return (
      nameError ||
      emailError ||
      messageError ||
      !formData.name ||
      !formData.email ||
      !formData.message
    );
  };
  const [imageUrl, setImageUrl] = useState<string>("");

  useEffect(() => {
    const newImageUrl =
      direction === "rtl"
        ? "https://ik.imagekit.io/yawakit/static_images/contact_mage_right.png?updatedAt=1741173686354"
        : "https://ik.imagekit.io/yawakit/static_images/contact_img.png?updatedAt=1741173587363";
    setImageUrl(newImageUrl);
  }, [direction]);
  useEffect(() => {
    if (window) {
      const timeoutid = setTimeout(() => {
        document.getElementById("contact")?.classList.add("scroll-mt");
      }, 100);
      return () => clearTimeout(timeoutid);
    }
  }, []);
  return (
    <section id="contact" ref={contactsRef} title="contact">
      <div className="md:flex md:flex-row m-8 p-0  bg-white rounded-[1rem] sm:rounded-[2rem] lg:rounded-[3rem]">
        {/* Image - 50% on desktop and full width on smaller screens */}
        <div className="w-full md:w-1/2 flex items-center justify-center overflow-hidden ">
          <ContactImage
            imageUrl={imageUrl}
            alt="contact-img"
            className={
              "w-full h-full flex-shrink-0 object-cover " +
              (direction === "rtl"
                ? "rounded-r-[1rem] sm:rounded-r-[2rem] lg:rounded-r-[3rem] rounded-l-none"
                : "rounded-l-[1rem] sm:rounded-l-[2rem] lg:rounded-l-[3rem] rounded-r-none")
            }
            key={imageUrl}
          />
        </div>

        {/* Form - 50% on desktop and full width on smaller screens */}
        <div className="w-full md:w-1/2 flex flex-col space-y-1 sm:space-y-2 lg:space-y-3 items-stretch p-0 sm:p-5 lg:p-8 justify-start px-2 lg:px-20">
          <div className="self-start">
            <h2 className={`text-[#233377] px-2  ${h2FontSize}`}>{translate("contact_title")}</h2>
            {/* <PageTitle title={translate("contact_title")} /> */}
          </div>
          <div className="flex flex-col">
            <label
              className="relative text-[#233377] max-w-max mx-2 font-medium text-[16px] sm:text-[18px]"
              htmlFor="name"
            >
              {translate("name")}*
            </label>
            <input
              className="outline-none p-3  h-[40px] sm:h-[50px] lg:h-[70px] rounded-xl bg-[#23337708] items-center"
              placeholder={translate("name")}
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={() => validateName(formData.name)}
            />
            {nameError && <div className="text-red">{translate(nameError)}</div>}
          </div>

          <div className="flex flex-col">
            <label
              className="relative text-[#233377] max-w-max mx-2 font-medium text-[16px] sm:text-[18px]"
              htmlFor="email"
            >
              {translate("email")}*
            </label>
            <input
              className="outline-none p-3 h-[40px] sm:h-[50px] lg:h-[70px] rounded-xl bg-[#23337708]"
              placeholder={translate("emailPlaceholder")}
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={() => validateEmail(formData.email)}
            />
            {emailError && <div className="text-red">{translate(emailError)}</div>}
          </div>

          <div className="flex flex-col">
            <label
              className="relative text-[#233377] max-w-max mx-2 font-medium text-[16px] sm:text-[18px]"
              htmlFor="message"
            >
              {translate("message")}*
            </label>
            <textarea
              rows={6}
              maxLength={2000}
              className="outline-none p-3 pt-2 max-h-20 sm:max-h-30 lg:max-h-40 rounded-xl bg-[#23337708]"
              placeholder={translate("message")}
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              onBlur={() => validateMessage(formData.message)}
            />
            {messageError && <div className="text-red">{translate(messageError)}</div>}
          </div>

          <div className="self-start">
            <ButtonFilled
              isDisabled={isInvalidForm()}
              mw="max-w-xs"
              padding="pl-10 pr-10"
              title={translate("contact_action")}
              type="button"
              onClick={submit}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
